import { FilterChainBuilder, Filter, ObservabilitySDK } from '@superbet-group/web.observability.sdk-core';

class DynamicHook {
    filters = [];
    chain;
    static getInstance() {
        if (!window.$$O11Y$$DynamicHook) {
            window.$$O11Y$$DynamicHook = new DynamicHook();
        }
        return window.$$O11Y$$DynamicHook;
    }
    static addFilter(filter) {
        DynamicHook.getInstance().addFilter(filter);
    }
    addFilter(filter) {
        this.filters = this.filters.filter(f => f.name() !== filter.name());
        this.filters.push(filter);
        this.createChain();
    }
    createChain() {
        this.chain = FilterChainBuilder.builder()
            .withFilters(this.filters)
            .addFilter(new DynamicHookDispatcherFilter())
            .build();
    }
    record(key, amount, attributes, options) {
        if (this.chain) {
            return this.chain.doFilter({
                key: key,
                amount: amount,
                attributes: attributes || {},
                options: options || {},
                context: {}
            }, {});
        }
        return Promise.resolve();
    }
}
class DynamicHookDispatcherFilter extends Filter {
    constructor() {
        super();
    }
    family() {
        return 'hooks';
    }
    order() {
        return Number.MAX_SAFE_INTEGER;
    }
    name() {
        /* istanbul ignore next */
        return 'DynamicHookFilter';
    }
    async doFilter(request, response, chain) {
        if (request.type === 'histogram') {
            ObservabilitySDK.recordHistogram(request.key, request.amount, request.attributes, request.options);
        }
        else if (request.type === 'gauge') {
            ObservabilitySDK.recordGauge(request.key, request.amount, request.attributes, request.options);
        }
        else {
            //FIXME Default type must be replaced by log once it get implemented
            ObservabilitySDK.recordCounter(request.key, request.amount || 1, request.attributes, request.options);
        }
        return await chain.doFilter(request, response);
    }
}

function recordCounter(metricName, amount = 1, labels, options) {
    ObservabilitySDK.recordCounter(metricName, amount, labels, options);
}
function recordGauge(metricName, amount, labels, options) {
    ObservabilitySDK
        .recordGauge(metricName, amount, labels, options);
}
function recordHistogram(metricName, amount, labels, options) {
    ObservabilitySDK
        .recordHistogram(metricName, amount, labels, options);
}
function record(metricName, amount, labels, options) {
    return log(metricName, amount, labels, options);
}
function log(metricName, amount, labels, options) {
    return DynamicHook.getInstance().record(metricName, amount, { ...labels }, { ...options });
}
const recordDurationSeconds = {};
const minStepDurationSeconds = 0.001;
function recordDurationSecondsStart(metricName, metricStepName, labels, options) {
    recordDurationSeconds[metricName] = {
        startedAt: new Date(),
        stepAt: new Date(),
        labels,
        options
    };
    return record(`${metricName}_${metricStepName}_total`, 1, labels, options);
}
function recordDurationSecondsStep(metricName, metricStepName, labels) {
    if (!recordDurationSeconds.hasOwnProperty(metricName))
        return;
    const mergedLabels = {
        ...recordDurationSeconds[metricName].labels,
        ...labels
    };
    const { stepAt, options } = recordDurationSeconds[metricName];
    const durationSeconds = Math.max(((new Date()).getTime() - stepAt.getTime()) / 1000, minStepDurationSeconds);
    recordDurationSeconds[metricName].stepAt = new Date();
    recordDurationSeconds[metricName].labels = mergedLabels;
    record(`${metricName}_${metricStepName}_duration_seconds`, durationSeconds, mergedLabels, options);
}
function recordDurationSecondsEnd(metricName, metricStepName) {
    if (!recordDurationSeconds.hasOwnProperty(metricName))
        return;
    recordDurationSecondsStep(metricName, metricStepName);
    const { startedAt, labels, options } = recordDurationSeconds[metricName];
    const durationSeconds = Math.max(((new Date()).getTime() - startedAt.getTime()) / 1000, minStepDurationSeconds);
    record(`${metricName}_duration_seconds`, durationSeconds, labels, options);
    delete recordDurationSeconds[metricName];
}

const DEFAULT_MAX_LENGTH = 255;
const DEFAULT_MIN_LENGTH = 1;
class DataValidationFilter extends Filter {
    validationConfig;
    name() {
        /* istanbul ignore next */
        return 'DataValidationFilter';
    }
    async doFilter(request, response, chain) {
        if (this.validationConfig && request.key) {
            const attributes = request.attributes;
            const invalidAttributes = [];
            const types = this.validationConfig.resolver(request.key);
            if ((!types || types.length == 0) && (!attributes || Object.keys(attributes).length === 0)) {
                return await chain.doFilter(request, response);
            }
            for (const type of types) {
                try {
                    if (!type.type || !(type.type.toLowerCase() === 'string' || type.type.toLowerCase() === 'number' || type.type.toLowerCase() === 'enum' || type.type.toLowerCase() === 'boolean')) {
                        console.warn(`ObservabilitySDK :: Data type [${type.type}] defined for action key[${request.key}] is invalid. Ignoring validation`);
                        continue;
                    }
                    if (!VALIDATORS[type.type](type, attributes[type.key])) {
                        invalidAttributes.push(type.key);
                        const newValue = CONVERT_INVALID_VALUE[type.type](type, attributes[type.key]);
                        if (newValue !== undefined) {
                            attributes[type.key] = newValue;
                        }
                        else if (type.key in attributes) {
                            delete attributes[type.key];
                        }
                    }
                }
                catch (e) {
                    console.warn(`ObservabilitySDK :: Fail to resolve required attributes for key[${request.key}]. Ignoring filter and proceeding`, e);
                }
            }
        }
        return await chain.doFilter(request, response);
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.validationConfig = filterConfig;
        }
    }
}
function stringValidation(dataType, labelValue) {
    const type = dataType;
    const value = labelValue;
    const minLength = type.minLength || DEFAULT_MIN_LENGTH;
    const maxLength = type.maxLength || DEFAULT_MAX_LENGTH;
    return !!value && value.length >= minLength && value.length <= maxLength;
}
function booleanValidation(dataType, value) {
    return !!value && typeof value == "boolean";
}
function numberValidation(dataType, value) {
    return !!value && typeof value === "number";
}
function enumValidation(dataType, labelValue) {
    const type = dataType;
    if (!labelValue || typeof labelValue !== "string" || !type.values || type.values.length == 0) {
        return false;
    }
    const options = type.values.map(option => option.toLowerCase());
    return options.indexOf(labelValue.toString().toLowerCase()) > -1;
}
function stringInvalidConverter(dataType, currentValue) {
    const type = dataType;
    if (currentValue && typeof currentValue !== "string") {
        currentValue = currentValue.toString();
    }
    if (!currentValue || typeof currentValue !== "string" || currentValue.length < type.minLength) {
        if (type.default) {
            return type.default.toString();
        }
        return type.default;
    }
    if (currentValue.length > type.maxLength) {
        return currentValue.substring(0, type.maxLength);
    }
}
function booleanInvalidConverter(type, currentValue) {
    if (currentValue === undefined) {
        return type.default;
    }
    if (typeof currentValue === "string") {
        return currentValue.toLowerCase() === 'true';
    }
    try {
        return Boolean(currentValue);
    }
    catch (e) {
        console.warn(`ObservabilitySDK :: Fail to parse value ${currentValue} to boolean`, e);
        return false;
    }
}
function numberInvalidConverter(type) {
    return type.default;
}
function enumInvalidConverter(type, currentValue) {
    if (currentValue && typeof currentValue !== "string") {
        const value = currentValue.toString();
        if (enumValidation(type, value)) {
            return value;
        }
    }
    return type.default !== undefined ? type.default.toString() : type.default;
}
const VALIDATORS = {
    string: stringValidation,
    boolean: booleanValidation,
    number: numberValidation,
    enum: enumValidation
};
const CONVERT_INVALID_VALUE = {
    string: stringInvalidConverter,
    boolean: booleanInvalidConverter,
    number: numberInvalidConverter,
    enum: enumInvalidConverter
};

//FIXME Default type must be replaced by log once it get implemented
const DEFAULT_TYPE$1 = 'counter';
class MetricOptionsResolverFilter extends Filter {
    optionsResolverConfig;
    name() {
        /* istanbul ignore next */
        return 'MetricOptionsResolverFilter';
    }
    async doFilter(request, response, chain) {
        if (this.optionsResolverConfig && request.key) {
            try {
                const resolvedType = this.optionsResolverConfig.resolver(request.key);
                if (resolvedType) {
                    request.options = resolvedType;
                }
            }
            catch (e) {
                console.error(`ObservabilitySDK :: Fail to resolve options for key[${request.key}]. Ignoring filter and proceeding`, e);
                request.type = DEFAULT_TYPE$1;
            }
        }
        await chain.doFilter(request, response);
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.optionsResolverConfig = filterConfig;
        }
    }
}

class RequiredAttributesFilter extends Filter {
    requiredConfig;
    name() {
        /* istanbul ignore next */
        return 'RequiredAttributesFilter';
    }
    async doFilter(request, response, chain) {
        if (this.requiredConfig && request.key) {
            try {
                const attributes = request.attributes;
                const requiredAttributes = this.requiredConfig.resolver(request.key);
                if (requiredAttributes.length > 0 && (!attributes || Object.keys(attributes).length === 0)) {
                    return this.rejectAction(requiredAttributes, request.key);
                }
                else {
                    const missingAttributes = [];
                    for (const attribute of requiredAttributes) {
                        if (!(attribute in attributes)) {
                            missingAttributes.push(attribute);
                        }
                    }
                    if (missingAttributes.length > 0) {
                        return this.rejectAction(missingAttributes, request.key);
                    }
                }
            }
            catch (e) {
                console.warn(`ObservabilitySDK :: Fail to resolve required attributes for key[${request.key}]. Ignoring filter and proceeding`, e);
            }
        }
        return await chain.doFilter(request, response);
    }
    rejectAction(requiredAttributes, key) {
        console.warn(`ObservabilitySDK :: Missing attributes [${requiredAttributes}] for key[${key}]. Ignoring action`);
        return Promise.reject(new Error(`ObservabilitySDK :: Missing attributes [${requiredAttributes}] for key[${key}]. Ignoring action`));
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.requiredConfig = filterConfig;
        }
    }
}

//FIXME Default type must be replaced by log once it get implemented
const DEFAULT_TYPE = 'counter';
class TypeResolverFilter extends Filter {
    typeResolverConfig;
    name() {
        /* istanbul ignore next */
        return 'TypeResolverFilter';
    }
    async doFilter(request, response, chain) {
        if (this.typeResolverConfig && request.key) {
            try {
                const resolvedType = this.resolveType(request.key);
                request.type = resolvedType || DEFAULT_TYPE;
            }
            catch (e) {
                console.error(`ObservabilitySDK :: Fail to resolve type for key[${request.key}]. Ignoring filter and proceeding`, e);
                request.type = DEFAULT_TYPE;
            }
        }
        await chain.doFilter(request, response);
    }
    resolveType(key) {
        return this.typeResolverConfig.resolver(key);
    }
    init(filterConfig) {
        super.init(filterConfig);
        if ('resolver' in filterConfig && typeof filterConfig.resolver === 'function') {
            this.typeResolverConfig = filterConfig;
        }
    }
}

export { DataValidationFilter, DynamicHook, DynamicHookDispatcherFilter, MetricOptionsResolverFilter, RequiredAttributesFilter, TypeResolverFilter, log, record, recordCounter, recordDurationSecondsEnd, recordDurationSecondsStart, recordDurationSecondsStep, recordGauge, recordHistogram };
//# sourceMappingURL=index.esm.js.map
